/**
 * AICoachBubble — Sitewide floating "Ask Coach" launcher.
 *
 * The gold pill stays visible on every page (except auth/onboarding and
 * pages with their own bottom composer). Tapping the pill toggles the
 * chat panel which floats above it via a React portal.
 *
 * State + API calls live in `useAICoach`. Panel JSX + composer live in
 * `CoachChatPanel`.
 */
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X } from 'lucide-react';
import { useAICoach } from './ai-coach/useAICoach';
import { CoachChatPanel } from './ai-coach/CoachChatPanel';

// Only hide on unauthenticated / onboarding screens. Everywhere else the
// pill stays visible so users can always summon the coach.
const HIDE_ON_PATHS = ['/login', '/register', '/signup', '/onboarding', '/forgot-password', '/reset-password'];

export const AICoachBubble = () => {
  const [open, setOpen] = useState(false);
  const coach = useAICoach(open);
  const { pathname } = useLocation();

  if (!coach.user) return null;
  if (HIDE_ON_PATHS.some((p) => pathname.startsWith(p))) return null;

  return (
    <>
      {/* Floating launcher — always visible, toggles the panel */}
      <motion.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        initial={{ opacity: 0, scale: 0.85, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-4 right-4 z-[1300] group flex items-center gap-1.5 pl-2 pr-3 py-2 rounded-full
          bg-gradient-to-br from-yellow-300 via-primary to-amber-500 text-black
          border border-yellow-200/70 shadow-[0_6px_20px_rgba(212,175,55,0.55),inset_0_1px_0_rgba(255,255,255,0.45)]
          hover:shadow-[0_10px_28px_rgba(212,175,55,0.8),inset_0_1px_0_rgba(255,255,255,0.6)]
          transition-shadow"
        data-testid="ai-coach-launcher"
        aria-label={open ? 'Close AI Coach chat' : 'Open AI Coach chat'}
        aria-expanded={open}
      >
        <span className="relative flex h-6 w-6 items-center justify-center rounded-full bg-black/85 text-primary
          shadow-[inset_0_0_0_1px_rgba(255,215,0,0.35)]">
          {open ? (
            <X className="h-3 w-3" strokeWidth={2.6} />
          ) : (
            <Sparkles className="h-3 w-3 animate-pulse" strokeWidth={2.2} />
          )}
        </span>
        <span className="font-heading uppercase tracking-[0.14em] text-[11px] whitespace-nowrap">
          {open ? 'Close' : 'Ask Coach'}
        </span>
        {!open && (
          <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-background animate-pulse" />
        )}
      </motion.button>

      {/* Chat panel (portal'd to <body> so it escapes any transformed parents) */}
      <AnimatePresence>
        {open && <CoachChatPanel coach={coach} onClose={() => setOpen(false)} />}
      </AnimatePresence>
    </>
  );
};

export default AICoachBubble;
